import { useEffect, useState, useCallback } from 'react';

import { useResponsive } from '@sg/garnish';

import { UPSELL_PLACEMENT_FLAG, type UpsellPlacement } from '@order/features-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useUpsellsV2 } from '../useUpsellsV2';

// ─────────────────────────────────────────────────────────────────────────────

export const useIsUpsellScreenEnabled = () => {
  const { minWidth } = useResponsive();

  return useFeatureFlag('CELS-4432-upsell-modal-order') && minWidth.isSM;
};

export const useUpsellPlacement = () => {
  const { getFeatureFlagValue, trackExperimentStarted, isInitialized } = useTelemetry();

  const isMixpanelExperimentEnabled = useFeatureFlag('cels-5775-use-mixpanel-exp');
  const shadowVariation = useFeatureFlag('cels-5775-upsell-exp-shadow');

  // ─── State ─────────────────────────────────────────────────────────────────

  const [mixpanelPlacement, setMixpanelPlacement] = useState<UpsellPlacement>();

  const isLoadingPlacement = isMixpanelExperimentEnabled && mixpanelPlacement === undefined;
  const placementVariation = isMixpanelExperimentEnabled ? mixpanelPlacement : shadowVariation;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const getMixpanelPlacement = useCallback(async () => {
    try {
      const placement = await getFeatureFlagValue<UpsellPlacement>(
        UPSELL_PLACEMENT_FLAG,
        'control',
      );

      setMixpanelPlacement(placement);
    } catch {
      setMixpanelPlacement('control');
    }
  }, [getFeatureFlagValue]);

  const trackShadowExperimentStarted = useCallback(() => {
    trackExperimentStarted({ experimentName: SHADOW_EXPERIMENT_ID, variantName: shadowVariation });
  }, [shadowVariation, trackExperimentStarted]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isMixpanelExperimentEnabled || !isInitialized) return;

    void getMixpanelPlacement();
  }, [isInitialized, getMixpanelPlacement, isMixpanelExperimentEnabled]);

  useEffect(() => {
    if (isMixpanelExperimentEnabled) return;

    trackShadowExperimentStarted();
  }, [isMixpanelExperimentEnabled, trackShadowExperimentStarted]);

  // ───────────────────────────────────────────────────────────────────────────

  return { placementVariation, isLoadingPlacement };
};

/**
 * Returns whether the upsell screen should be shown based on the placement variant,
 * screen size, and whether there are upsell products available.
 */
export const useShouldShowUpsellScreen = () => {
  const isUpsellScreenEnabled = useIsUpsellScreenEnabled();
  const { placementVariation, isLoadingPlacement } = useUpsellPlacement();

  const { upsellProducts, isLoadingUpsells } = useUpsellsV2();

  const hasUpsellProducts = !isLoadingUpsells && upsellProducts.length > 0;
  const isLoading = isLoadingPlacement || (isLoadingUpsells && placementVariation === 'control');

  return {
    isLoading,
    shouldShowUpsellScreen:
      isUpsellScreenEnabled && hasUpsellProducts && placementVariation === 'control',
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SHADOW_EXPERIMENT_ID = 'upsell_placement_v3_shadow' as const;
